import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import apiCall from '../../api';
import {
    CustomerDetails,
    CollectiveInvoice,
    InvoicePreferences,
    ItemDetails,
    OtherDetails
} from './index';
import '../scss/create-invoice.scss';
import '../scss/invoice-wizard.scss';
import InvoiceWizardStepper, { WizardStep } from './invoice-wizard-stepper.component';
import { formatAmountWithLocale } from '../helper/common.helper';
import WizardSummary from './invoice-wizard-summary.component';
import { useInvoiceCalculations } from '../hooks/useInvoiceCalculations';
import { useAppSelector, useAppDispatch } from '../hooks/useRedux';
import { setSelectedFreelancersForInvoice, setShowFreelancerNamesOnPdf } from '../features/customer/customer.slice';
import { toast } from 'react-toastify';
import { CopyRecentInvoiceModal } from './copy-recent-invoice-modal';
import { getInvoiceDetailForEditApi } from '../features/invoice/invoice.service';
import { uploadAttachment } from '../utils/presigned-upload.utils';

interface InvoiceFormData {
    customer: {
        name: string;
        id: string;
        billingAddress: string;
        accountType: string;
    };
    preferences: {
        currency: string;
        engagementType: string;
        pricingType: string;
        vat: number;
        showDates?: string;
    };
    items: Array<{
        id: string;
        name: string;
        quantity: number;
        price: number;
        discount: {
            value: number;
            type: 'percentage' | 'fixed';
        };
        subtotal: number;
    }>;
    otherDetails: {
        customerNotes: string;
        supportingFiles: Array<{
            fileName: string;
            // Absent for an already-saved attachment loaded for edit - only a
            // fileName exists then (bytes never travel over the wire).
            file?: File;
            uploaded?: boolean;
        }>;
        communicationEmails: string[];
    };
}

// Fields the calculation writes (line totals, tax/net, and every SEK-converted
// amount). They're excluded — and numbers rounded — when snapshotting for the
// edit-mode dirty check, so a fresh recompute never differs from the loaded
// baseline. Without this, reverting a change wouldn't re-disable the Edit Request
// button because a recomputed amount rarely matches the stored one byte-for-byte.
const DIRTY_IGNORE_KEYS = new Set<string>([
    // `dateId` is `${date}-${uuidv4()}` and `createdDate` is a fresh ISO timestamp on
    // every payload build — both non-deterministic, so they MUST be ignored or the
    // snapshot never matches the baseline after a revert.
    'dateId', 'createdDate',
    'amount', 'net', 'tax', 'vatAmount', 'amountIncludingVat', 'totalAmount',
    'grossSalary', 'taxableAmount', 'holidayPayAmount', 'expressFee',
    'rutDeductionAmount', 'rootDeductionAmount', 'currencyRate',
]);

// A value that carries no meaning for change-detection — treated as "not there" so two
// representations of the same data (e.g. a server-loaded deduction vs the same one
// re-built by the frontend transform) reduce to the same meaningful core.
const isBlankForDirty = (v: any): boolean =>
    v === null || v === undefined || v === '' || v === false || v === 0 || v === '0' ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length === 0);

// Deep-canonicalise the invoice state into a stable string that depends ONLY on the
// user's meaningful inputs, so reverting a change always matches the baseline again and
// re-disables the Edit Request button. It:
//   • sorts object keys        → immune to objects being rebuilt in a different order
//   • sorts arrays by content  → a line that moves position (e.g. an edited deduction
//                                re-appended at the end) still matches the baseline
//   • drops computed/SEK fields → a fresh recompute never counts as a change
//   • rounds numbers (2 dp)     → absorbs float-precision drift
//   • omits blank/default vals  → 0 / '0' / false / '' / empty read as "not there", so
//                                 the server shape and the transform shape of the same
//                                 deduction collapse to the same core.
const canonicalizeForDirty = (input: any): any => {
    if (Array.isArray(input)) {
        return input
            .map(canonicalizeForDirty)
            .sort((a, b) => {
                const sa = JSON.stringify(a);
                const sb = JSON.stringify(b);
                return sa < sb ? -1 : sa > sb ? 1 : 0;
            });
    }
    if (input && typeof input === 'object') {
        const out: Record<string, any> = {};
        for (const key of Object.keys(input).sort()) {
            if (DIRTY_IGNORE_KEYS.has(key)) continue;
            if (/(InSek|InSEK|Sek|SEK)$/.test(key)) continue; // currency-converted computed values
            const v = canonicalizeForDirty(input[key]);
            if (isBlankForDirty(v)) continue;
            out[key] = v;
        }
        return out;
    }
    if (typeof input === 'number') return Math.round(input * 100) / 100;
    return input;
};

// Reduce the built payload to ONLY the user-meaningful fields before diffing. A
// server-loaded line carries server metadata (invoiceId, articleName, createdBy, …)
// that the same line rebuilt by the frontend transform does not — so comparing raw
// lines never matches even when the data is identical. Projecting both to this
// allowlist makes them comparable, so reverting a change re-matches the baseline.
const DIRTY_WH_FIELDS = [
    'articleNumber', 'workingDate', 'number', 'unit', 'vat', 'amountPerUnit',
    'workingDescription', 'CustomWorkingDescription', 'houseworkType', 'workingHour',
    'isArticleShownInInvoice', 'isTaxFreeArticle', 'isPdfAttachedToInvoice',
    'distance', 'reasonForTrip', 'carRegistrationNo', 'tripStartedPlace', 'tripEndedPlace',
    'tripStartedDate', 'tripEndedDate', 'country', 'allowanceDestination', 'allowancePerDay',
    'totalDayOfTravels', 'isHalfDay', 'expenseCategory', 'foodAllowance', 'fileName',
    'userId',
    // NOTE: `id` / `timeSheetWorkingHourId` are deliberately excluded — they're identity
    // metadata that can differ between a server-loaded line and the same line rebuilt by
    // the frontend (server id vs preserved/0), which would falsely read as a change.
    // Content fields above are enough to detect real edits and to re-match on revert.
];
const DIRTY_PAYLOAD_FIELDS = [
    'vat', 'currency', 'customerId', 'email', 'secondaryEmailAddress', 'description',
    'pension', 'pensionAmount', 'pensionFee', 'holidaySavings', 'holidaySavingsFee',
    'rutDeduction', 'rootDeduction', 'house', 'apartment', 'brfNo', 'rotApartmentNo',
    'showDates', 'expressPayout', 'dueDate', 'hideFreelancerName', 'multipleFreelancer',
];
const projectPayloadForDirty = (p: any): any => {
    if (!p || typeof p !== 'object') return p;
    const pick = (obj: any, keys: string[]) => {
        const o: Record<string, any> = {};
        for (const k of keys) if (obj[k] !== undefined) o[k] = obj[k];
        return o;
    };
    const out: Record<string, any> = pick(p, DIRTY_PAYLOAD_FIELDS);
    out.workingHours = Array.isArray(p.workingHours) ? p.workingHours.map((w: any) => pick(w, DIRTY_WH_FIELDS)) : [];
    out.invoiceFreelancer = Array.isArray(p.invoiceFreelancer)
        ? p.invoiceFreelancer.map((f: any) => ({ userId: f?.userId ?? f?.freelancerId }))
        : [];
    return out;
};

// The invoice payload only carries the customerId — editing a customer's DETAILS
// (name, address, email, VAT, org no, …) doesn't change the payload, so those edits
// go undetected. Snapshot the selected customer's meaningful fields separately so any
// customer change flips the dirty flag (and reverting it flips it back).
const DIRTY_CUSTOMER_FIELDS = [
    'name', 'orgNo', 'ssn', 'reference', 'email', 'secondaryEmailAddress',
    'address', 'address2', 'billingAddress', 'co', 'city', 'zipCode', 'country',
    'countryCode', 'vatNumber', 'paymentTerm', 'language', 'isVATRegistred',
    'accountType', 'invoiceType', 'phoneNumber', 'phoneNo',
];
const projectCustomerForDirty = (c: any): any => {
    if (!c || typeof c !== 'object') return {};
    const o: Record<string, any> = {};
    for (const k of DIRTY_CUSTOMER_FIELDS) if (c[k] !== undefined) o[k] = c[k];
    return o;
};

interface CreateInvoiceProps {
    dataRef?: React.MutableRefObject<any>;
    isEditMode?: boolean;
    preId?: string | null;
    validateServiceDescriptionsRef?: React.MutableRefObject<{[freelancerId: string]: () => boolean} | undefined>;
    validateDatesRef?: React.MutableRefObject<{[freelancerId: string]: () => boolean} | undefined>;
    copiedInvoiceData?: any;
    onCopiedInvoiceDataChange?: (data: any) => void;
    onCalculatedSubAmountChange?: (data: any) => void;
    onDeductionsByFreelancerChange?: (data: any) => void;
    onPreferencesChange?: (preferences: any) => void; // NEW - Callback for preferences changes
    timesheetData?: any[];  // Timesheet data from Redux
    invoiceNumber?: number; // Invoice number from URL
    onStepChange?: (step: number, total: number, title?: string, subtitle?: string) => void; // NEW - report active wizard step (+ its live context) to parent
    backHandlerRef?: React.MutableRefObject<(() => void) | null>; // NEW - exposes wizard "Back" so the page footer can trigger it
    onDirtyChange?: (isDirty: boolean) => void; // NEW (edit mode) - true once the user changes anything, so the Edit Request button can enable
    getPayload?: () => Promise<any> | any; // NEW (edit mode) - builds the merged outgoing payload; used to detect real changes reliably
}

export const CreateInvoice: React.FC<CreateInvoiceProps> = ({ 
    dataRef, 
    isEditMode, 
    preId, 
    copiedInvoiceData: copiedInvoiceDataProp,
    onCopiedInvoiceDataChange,
    onCalculatedSubAmountChange,
    onDeductionsByFreelancerChange,
    onPreferencesChange,
    onDirtyChange,
    getPayload,
    validateServiceDescriptionsRef,
    validateDatesRef,
    timesheetData: timesheetDataProp,
    invoiceNumber = 0,
    onStepChange,
    backHandlerRef
}) => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    // Freelancers selected for the invoice (used by the Step 5 summary breakdown)
    const selectedFreelancersForInvoice = useAppSelector((state: any) => state?.customer?.selectedFreelancersForInvoice || []);
    // Selected customer (+ full record from the list) — used by dirty-detection so
    // editing customer details enables the Edit Request button.
    const selectedCustomerRedux = useAppSelector((state: any) => state?.customer?.selectedCustomer);
    const customersList = useAppSelector((state: any) => state?.customer?.customers || []);
    
    // Get timesheet data from Redux if not provided as prop
    const timesheetDataFromRedux = useAppSelector((state: any) => state?.timesheet?.timesheetData);
    const timesheetData = timesheetDataProp || timesheetDataFromRedux;
    const dispatch = useAppDispatch();
    
    // Get globalData from Redux
    const globalData = useAppSelector((state: any) => state?.globalData?.data);
    
    // State to track calculated sub amount from ItemDetails
    const [calculatedSubAmount, setCalculatedSubAmount] = useState<any>(null);
    const prefillingData = useAppSelector((state: any) => state?.auth);
    const user = useAppSelector((state: any) => state?.auth?.user);
    const selectedInvoiceDetails = useAppSelector((state: any) => state?.invoice?.selectedInvoiceDetails);
    const selectedInvoiceLoading = useAppSelector((state: any) => state?.invoice?.selectedInvoiceLoading);
    
    // Extract createdDate from URL parameters
    const searchParams = new URLSearchParams(location.search);
    const createdDate = searchParams.get('createdDate') || undefined;
    
    // Logo upload state
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [userLogo, setUserLogo] = useState<string | null>(null);
    const [logoLoading, setLogoLoading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const logoInputRef = React.useRef<HTMLInputElement>(null);
    // Default Invozio logo shown when the user has none / after removing theirs.
    const DEFAULT_LOGO = '/assets/svg/invozio-logo.svg';

    // Copy recent invoice modal state
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [copiedInvoiceData, setCopiedInvoiceData] = useState<any>(copiedInvoiceDataProp || null);
    
    // Ref to track if draft invoice has been loaded (prevent infinite loop)
    const draftInvoiceLoadedRef = React.useRef<string | null>(null);
    
    // Ref to track the last sent copiedInvoiceData to prevent unnecessary updates
    const lastSentDataRef = React.useRef<any>(null);
    
    // Ref to track the last sent calculatedSubAmount to prevent unnecessary updates
    const lastSentSubAmountRef = React.useRef<any>(null);

    // Ref to track previous URL mode to prevent reset on language-only changes
    const previousLocationModeRef = React.useRef<{
        hasEdit: boolean;
        hasPreId: boolean;
        hasTimesheet: boolean;
        hasInvoiceNumber: boolean;
    } | null>(null);

    // Sync with prop changes
    useEffect(() => {
        if (copiedInvoiceDataProp) {
            setCopiedInvoiceData(copiedInvoiceDataProp);
        }
    }, [copiedInvoiceDataProp]);

    // Send copiedInvoiceData updates to parent (only when data actually changes)
    useEffect(() => {
       
        if (onCopiedInvoiceDataChange && copiedInvoiceData && copiedInvoiceData !== lastSentDataRef.current) {
            lastSentDataRef.current = copiedInvoiceData;
            onCopiedInvoiceDataChange(copiedInvoiceData);
        }
    }, [copiedInvoiceData]);

    // Pass calculatedSubAmount to parent when it changes (only when data actually changes)
    useEffect(() => {
        if (onCalculatedSubAmountChange && calculatedSubAmount !== lastSentSubAmountRef.current) {
            lastSentSubAmountRef.current = calculatedSubAmount;
            onCalculatedSubAmountChange(calculatedSubAmount);
        }
    }, [calculatedSubAmount]);

    // Fetch user logo from server
    const fetchUserLogo = useCallback(async () => {
        if (!user?.userId) return;
        
        try {
            setLogoLoading(true);
            const response = await apiCall.get(`/signup/invoice-logo-stream?userId=${user.userId}`, {
                responseType: 'blob'
            });
            
            if (response.status === 200 && response.data.size > 0) {
                const logoBlob = response.data;
                const logoUrl = URL.createObjectURL(logoBlob);
                setUserLogo(logoUrl);
                setLogoPreview(logoUrl);
            }
        } catch (error: any) {
            if (error.response?.status === 404) {
                // No custom logo → show the default Invozio logo by default.
                setUserLogo(DEFAULT_LOGO);
                setLogoPreview(DEFAULT_LOGO);
            } else {
                console.error('Error fetching user logo:', error);
                setUserLogo(DEFAULT_LOGO);
                setLogoPreview(DEFAULT_LOGO);
            }
        } finally {
            setLogoLoading(false);
        }
    }, [user?.userId]);

    // Fetch logo on component mount
    useEffect(() => {
        fetchUserLogo();
    }, [fetchUserLogo]);

    // Handle logo upload
    const handleLogoClick = () => {
        logoInputRef.current?.click();
    };

    const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const logoFormat = file.name.split('.').pop()?.toLowerCase();
        if (!file.type.startsWith('image/') || !(logoFormat === 'png' || logoFormat === 'jpg' || logoFormat === 'jpeg')) {
            toast.error(t('LOGO_FORMAT_NOT_SUPPORTED') as string);
            return;
        }

        // Validate file size (max 1MB - matching PersonalData.js)
        if (file.size > 1024 * 1024) {
            toast.error(t('FILE_TOO_LARGE_1MB') as string);
            return;
        }

        try {
            setLogoLoading(true);
            
            // Upload logo to server
            const formData = new FormData();
            formData.append('file', file);

            const uploadResponse = await apiCall.post('/signup/invoice-logo', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (uploadResponse.status === 200) {
                // Fetch the uploaded logo
                await fetchUserLogo();
                toast.success(t('LOGO_UPLOADED_SUCCESSFULLY') as string);
                
                // Clear the file input
                if (logoInputRef.current) {
                    logoInputRef.current.value = '';
                }
            } else {
                toast.error(t('FAILED_TO_UPLOAD_LOGO') as string);
            }
        } catch (error: any) {
            console.error('Logo upload failed:', error);
            const errorMessage = error?.response?.data?.message || t('FAILED_TO_UPLOAD_LOGO');
            toast.error(errorMessage as string);
        } finally {
            setLogoLoading(false);
        }
    };

    const handleRemoveLogo = async (event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent triggering file input
        
        if (!user?.userId) {
            toast.error(t('USER_NOT_FOUND') as string);
            return;
        }

        try {
            setLogoLoading(true);
            
            const deleteResponse = await apiCall.delete(`/signup/user-logo?userId=${user.userId}`);
            
            if (deleteResponse.status === 200) {
                // Clean up the blob URL if it exists
                if (userLogo) {
                    URL.revokeObjectURL(userLogo);
                }
                
                setUserLogo(DEFAULT_LOGO);
                setLogoPreview(DEFAULT_LOGO);   // revert to the Invozio default logo

                if (logoInputRef.current) {
                    logoInputRef.current.value = '';
                }

                toast.success(t('LOGO_DELETED_SUCCESSFULLY') as string);
            } else {
                toast.error(t('FAILED_TO_DELETE_LOGO') as string);
            }
        } catch (error: any) {
            console.error('Logo deletion failed:', error);
            const errorMessage = error?.response?.data?.message || t('FAILED_TO_DELETE_LOGO');
            toast.error(errorMessage as string);
        } finally {
            setLogoLoading(false);
        }
    };

    // Handle drag events for logo upload
    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragIn = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setDragActive(true);
        }
    };

    const handleDragOut = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (logoLoading) return;
        
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            
            // Validate file type
            const logoFormat = file.name.split('.').pop()?.toLowerCase();
            if (!file.type.startsWith('image/') || !(logoFormat === 'png' || logoFormat === 'jpg' || logoFormat === 'jpeg')) {
                toast.error(t('LOGO_FORMAT_NOT_SUPPORTED') as string);
                return;
            }

            // Validate file size (max 1MB)
            if (file.size > 1024 * 1024) {
                toast.error(t('FILE_TOO_LARGE_1MB') as string);
                return;
            }

            try {
                setLogoLoading(true);
                
                // Upload logo to server
                const formData = new FormData();
                formData.append('file', file);

                const uploadResponse = await apiCall.post('/signup/invoice-logo', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (uploadResponse.status === 200) {
                    // Fetch the uploaded logo
                    await fetchUserLogo();
                    toast.success(t('LOGO_UPLOADED_SUCCESSFULLY') as string);
                } else {
                    toast.error(t('FAILED_TO_UPLOAD_LOGO') as string);
                }
            } catch (error: any) {
                console.error('Logo upload failed:', error);
                const errorMessage = error?.response?.data?.message || t('FAILED_TO_UPLOAD_LOGO');
                toast.error(errorMessage as string);
            } finally {
                setLogoLoading(false);
            }
        }
    };

    // Handle edit mode from URL params
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const isEditMode = searchParams.get('edit') === 'true';
        const invoiceNumber = searchParams.get('invoiceNumber');
        const preId = searchParams.get('preId'); // Draft invoice ID
        
        // Handle regular invoice edit mode
        if (isEditMode && invoiceNumber) {
          // Fetch invoice detail for edit
            const fetchInvoiceForEdit = async () => {
                try {
                    // toast.info(String(t('LOADING_INVOICE_DATA') || 'Loading invoice data...'));
                    
                    const invoiceData = await getInvoiceDetailForEditApi(invoiceNumber);
                    // Populate the Collaborators toggle from the saved invoice's
                    // hideFreelancerName (the toggle mirrors it directly).
                    const savedHideFreelancerName = (invoiceData as any).hideFreelancerName;
                    if (typeof savedHideFreelancerName === 'boolean') {
                        dispatch(setShowFreelancerNamesOnPdf(savedHideFreelancerName));
                    }
                    // If it's a multiple freelancer invoice, populate selectedFreelancersForInvoice
                    if (invoiceData.multipleFreelancer && invoiceData.invoiceFreelancer && invoiceData.invoiceFreelancer.length > 0) {
                        
                        // Map invoiceFreelancer to the format expected by UI
                        const freelancers = invoiceData.invoiceFreelancer.map((freelancer: any) => ({
                            userId: freelancer.userId,
                            id: freelancer.userId, // Use userId as id for consistency
                            name: `${freelancer.freelancerDetails?.firstName || ''} ${freelancer.freelancerDetails?.lastName || ''}`.trim(),
                            userName: `${freelancer.freelancerDetails?.firstName || ''} ${freelancer.freelancerDetails?.lastName || ''}`.trim(),
                            email: freelancer.freelancerDetails?.email || '',
                            ...freelancer.freelancerDetails
                        }));
                        
                        dispatch(setSelectedFreelancersForInvoice(freelancers));
                    }
                    
                    // Set the copied invoice data which will trigger population in child components
                    setCopiedInvoiceData(invoiceData);

                    // Carry the top-level attachment's blob fileName forward into formData
                    // itself (not just local display state in OtherDetails), so an edit
                    // save that doesn't touch this file still resubmits it instead of
                    // silently dropping it.
                    const existingTopLevelFile = (invoiceData as any)?.file?.[0];
                    if (existingTopLevelFile?.fileName) {
                        setFormData(prev => ({
                            ...prev,
                            otherDetails: {
                                ...prev.otherDetails,
                                supportingFiles: [{ fileName: existingTopLevelFile.fileName, uploaded: true }]
                            }
                        }));
                    }

                    toast.success(String(t('INVOICE_DATA_LOADED') || 'Invoice data loaded successfully'));
                } catch (error: any) {
                    console.error('❌ Error fetching invoice for edit:', error);
                    toast.error(String(t('FAILED_TO_LOAD_INVOICE')));
                }
            };
            
            fetchInvoiceForEdit();
        }
    }, [location.search]);
    
    // Monitor when draft invoice API is loading - reset ref to allow fresh load when data arrives
    useEffect(() => {
        if (selectedInvoiceLoading) {
            const searchParams = new URLSearchParams(location.search);
            const preId = searchParams.get('preId');
            
            if (preId) {
                // Reset the ref while loading so when data arrives, we set it fresh
                draftInvoiceLoadedRef.current = null;
            }
        }
    }, [selectedInvoiceLoading, location.search]);
    
    // Separate useEffect to load draft invoice when selectedInvoiceDetails becomes available
    // This runs independently and only once per preId
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const preId = searchParams.get('preId');
        
        // Only run if we have preId, selectedInvoiceDetails is available, and we haven't loaded it yet
        if (preId && selectedInvoiceDetails && draftInvoiceLoadedRef.current !== preId) {
            draftInvoiceLoadedRef.current = preId;

            // Populate the Collaborators toggle from the draft's hideFreelancerName
            // (the toggle mirrors it directly).
            const savedHideFreelancerName = (selectedInvoiceDetails as any)?.hideFreelancerName;
            if (typeof savedHideFreelancerName === 'boolean') {
                dispatch(setShowFreelancerNamesOnPdf(savedHideFreelancerName));
            }

            // Create a fresh copy to ensure React detects the change
            setCopiedInvoiceData({ ...selectedInvoiceDetails });

            // Same reasoning as the invoice-edit load above: carry the draft's
            // top-level attachment fileName into formData itself so it survives
            // a re-save that doesn't touch it.
            const existingTopLevelFile = (selectedInvoiceDetails as any)?.file?.[0];
            if (existingTopLevelFile?.fileName) {
                setFormData(prev => ({
                    ...prev,
                    otherDetails: {
                        ...prev.otherDetails,
                        supportingFiles: [{ fileName: existingTopLevelFile.fileName, uploaded: true }]
                    }
                }));
            }

            toast.success(String(t('DRAFT_INVOICE_DATA_LOADED') || 'Draft invoice data loaded successfully'));
        } else if (!preId) {
            // Reset ref if preId is cleared
            draftInvoiceLoadedRef.current = null;
        }
    }, [selectedInvoiceDetails, location.search]);
    
    // Invoice Calculations Hook
    const { 
        calculateMultipleUsers, 
        loading: calcLoading, 
        calculationData, 
        error: calcError,
        clearData: clearCalculationData 
    } = useInvoiceCalculations();
    
    // React Hook Form setup
    const {
        control,
        register,
        watch,
        setValue,
        handleSubmit,
        trigger,
        getValues,
        setError,
        formState: { errors },
        reset: resetForm
    } = useForm<{
        accommodation: string;
        foodAllowance: string;
        mileage: number;
        workDates: string;
        serviceType: string;
        workTime: string;
        hourlyPrice: number;
        pension: boolean;
        holidaySavings: boolean;
        expressPayout: boolean;
        currency: string;
        vat: number;
        amount: number;
        invoiceHours: any[];
        selectedCustomer: any;
        isFixed: boolean;
        rutDeduction: boolean;
        rootDeduction: boolean;
        showDates: string;
    }>({
        defaultValues: {
            accommodation: '',
            foodAllowance: '',
            mileage: 0,
            workDates: '',
            serviceType: '',
            workTime: '',
            hourlyPrice: 0,
            pension: false,
            holidaySavings: false,
            expressPayout: false,
            currency: 'SEK',
            vat: 25,
            amount: 0,
            invoiceHours: [],
            selectedCustomer: null,
            isFixed: false,
            rutDeduction: false,
            rootDeduction: false,
            showDates: 'yes',
        }
    });

    // Reset component state when creating fresh invoice (no edit/preId/timesheet params)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const hasEditParam = searchParams.get('edit') === 'true';
        const hasPreIdParam = searchParams.get('preId');
        const hasTimesheetParam = searchParams.get('timesheet') === 'true';
        const hasInvoiceNumber = searchParams.get('invoiceNumber');

        const currentMode = {
            hasEdit: hasEditParam,
            hasPreId: !!hasPreIdParam,
            hasTimesheet: hasTimesheetParam,
            hasInvoiceNumber: !!hasInvoiceNumber,
        };

        // If no special params, this is a fresh invoice creation - reset all component states
        const isFreshCreation = !hasEditParam && !hasPreIdParam && !hasTimesheetParam && !hasInvoiceNumber;

        const hadPreviousMode = previousLocationModeRef.current;
        let shouldReset = false;
        if (!hadPreviousMode && isFreshCreation) {
            // First load in fresh creation mode
            shouldReset = true;
        } else if (hadPreviousMode) {
            const wasInSpecialMode = hadPreviousMode.hasEdit || hadPreviousMode.hasPreId ||
                                     hadPreviousMode.hasTimesheet || hadPreviousMode.hasInvoiceNumber;
            // Only reset when transitioning from a special mode to fresh creation
            if (wasInSpecialMode && isFreshCreation) {
                shouldReset = true;
            }
        }

        previousLocationModeRef.current = currentMode;

        if (shouldReset) {
            // Reset React Hook Form
            resetForm({
                accommodation: '',
                foodAllowance: '',
                mileage: 0,
                workDates: '',
                serviceType: '',
                workTime: '',
                hourlyPrice: 0,
                pension: false,
                holidaySavings: false,
                expressPayout: false,
                currency: 'SEK',
                vat: 25,
                amount: 0,
                invoiceHours: [],
                selectedCustomer: null,
                isFixed: false,
                rutDeduction: false,
                rootDeduction: false,
                showDates: 'yes',
            });

            // Reset all local states
            setCopiedInvoiceData(null);
            setFormData({
                customer: { name: '', id: '', billingAddress: '', accountType: '' },
                preferences: { currency: 'SEK', engagementType: '', pricingType: '', vat: 25, showDates: 'yes' },
                items: [],
                otherDetails: { customerNotes: '', supportingFiles: [], communicationEmails: [] }
            });
            setServiceTableDataByFreelancer({});
            setPensionDataByFreelancer({});
            setHolidayDataByFreelancer({});
            setDeductionsByFreelancer({});
            setCalculatedAmounts({
                net: 0, tax: 0, amount: 0, pensionPercentage: 0,
                pension: 0, holidayPayAmount: 0, expressFee: 0,
                rutDeductionAmount: 0, rootDeductionAmount: 0,
            });
            setCalculatedSubAmount(null);
            setSelectedList([{}]);
            setCustomer({});
            setNewEmail('');
            setGetCalculations(false);
            setServiceTableTrigger(false);
            
            // Reset refs
            draftInvoiceLoadedRef.current = null;
            lastSentDataRef.current = null;
            lastSentSubAmountRef.current = null;

            // Clear calculation data
            if (clearCalculationData) {
                clearCalculationData();
            }
        }
    }, [location.search, resetForm, clearCalculationData]);

    // Watch form values
    const vat = watch('vat');
    const amount = watch('amount');
    const watchedCurrency = watch('currency') || 'SEK';
    const pension = watch('pension');
    const holidaySavings = watch('holidaySavings');
    const expressPayout = watch('expressPayout');
    const invoiceHours = watch('invoiceHours');
    const selectedCustomer = watch('selectedCustomer');
    const isFixed = watch('isFixed');
    const rutDeduction = watch('rutDeduction');
    const rootDeduction = watch('rootDeduction');
    const showDates = watch('showDates');

    const [formData, setFormData] = useState<InvoiceFormData>({
        customer: {
            name: '',
            id: '',
            billingAddress: '',
            accountType: ''
        },
        preferences: {
            currency: 'SEK',
            engagementType: '',
            pricingType: '',
            vat: 25,
            showDates: 'yes'
        },
        items: [],
        otherDetails: {
            customerNotes: '',
            supportingFiles: [],
            communicationEmails: []
        }
    });

    const [newEmail, setNewEmail] = useState('');
    // Tracks the supporting-file upload triggered on pick (see handleSupportingFileChange)
    // so OtherDetails can show a loading state instead of a stale/blank filename.
    const [isUploadingSupportingFile, setIsUploadingSupportingFile] = useState(false);
    const [showTooltipError, setShowTooltipError] = useState<{[key: string]: boolean}>({});
    const [customer, setCustomer] = useState<any>({});
    const [selectedList, setSelectedList] = useState<any[]>([{}]);
    
    // Use watchedCurrency as the main currency value
    const currency = watchedCurrency;
    
    // Calculation-related state (matching InvoiceTool.js)
    const [calculatedAmounts, setCalculatedAmounts] = useState({
        net: 0,
        tax: 0,
        amount: 0,
        pensionPercentage: 0,
        pension: 0,
        holidayPayAmount: 0,
        expressFee: 0,
        rutDeductionAmount: 0,
        rootDeductionAmount: 0,
    });
    
    const [seperatedUserCalculations, setSeperatedUserCalculations] = useState<any[]>([
        {
            net: 0,
            tax: 0,
            amount: 0,
            pensionPercentage: 0,
        },
    ]);
    
    const [otherArticlesData, setOtherArticlesData] = useState<any>({});
    const [allowancePerDaySweden, setAllowancePerDaySweden] = useState({
        allowance_in: 0,
        allowance_out: 0,
    });
    const [totalHours, setTotalHours] = useState<number[]>([0]);
    const [articleHourlyAmount, setArticleHourlyAmount] = useState(0);
    const [pensionAmount, setPensionAmount] = useState(1000);
    const [pensionFee, setPensionFee] = useState(5);
    const [holidaySavingFee, setHolidaySavingFee] = useState(12);
    const [getCalculations, setGetCalculations] = useState(false);
    const [dates, setDates] = useState<any>({});
    const [grossSalary, setGrossSalary] = useState(0);
    const [isPre, setIsPre] = useState(false);
    const [serviceTableTrigger, setServiceTableTrigger] = useState(false); // Trigger for service table changes
    const [serviceTableDataByFreelancer, setServiceTableDataByFreelancer] = useState<any>({}); // NEW - Service table data by freelancer

    // RUT/ROT is only allowed when the invoice has at least one "Payment per Hour" (hourly, article 92) article.
    const hasHourlyArticle = React.useMemo(() => {
        return Object.values(serviceTableDataByFreelancer || {}).some((data: any) =>
            Array.isArray(data?.workingHours) &&
            data.workingHours.some((wh: any) => String(wh?.articleNumber) === '92')
        );
    }, [serviceTableDataByFreelancer]);

    const [pensionDataByFreelancer, setPensionDataByFreelancer] = useState<any>({}); // NEW - Pension data by freelancer
    const [holidayDataByFreelancer, setHolidayDataByFreelancer] = useState<any>({}); // NEW - Holiday data by freelancer
    const [deductionsByFreelancer, setDeductionsByFreelancer] = useState<any>({}); // NEW - Deductions by freelancer
    
    // Mock data - in real app this would come from API/props
    const mockUsers = [
        { userId: '1', firstName: 'Kevin', lastName: 'Zad', email: 'kevinzad123@gmail.com' },
        { userId: '2', firstName: 'Hanna', lastName: 'Deetko', email: 'hannadeetko@gmail.com' }
    ];
    
    const mockArticles = [
        { articleNumber: '01', articleName: 'Hourly Work', id: '1' },
        { articleNumber: '02', articleName: 'Fixed Price', id: '2' },
        { articleNumber: '03', articleName: 'Consultation', id: '3' }
    ];

    // Get currency global value from globalData
    const currencyGlobalValue = globalData?.currencies?.find(
        (item: any) => item.description === currency
    )?.globalValue || 1;

    // Helper functions (from InvoiceTool.js)
    const hasAnyValue = (obj: any) => Object.values(obj).some((array: any) => Array.isArray(array) && array.length > 0);
    const anyArrayHasValue = hasAnyValue(otherArticlesData);
    
    const calculateTotalSum = (data: any, currencyValue: number) => {
        // Implementation from InvoiceTool.js common/FunctionUtils
        let total = 0;
        Object.values(data || {}).forEach((articles: any) => {
            if (Array.isArray(articles)) {
                articles.forEach((article: any) => {
                    if (article?.isArticleShowInInvoice) {
                        const value = parseFloat(article?.value) || 0;
                        total += value / (currencyValue || 1);
                    }
                });
            }
        });
        return total;
    };
    
    const calculateExpenditureSum = (data: any) => {
        // Implementation for tax-free materials
        let total = 0;
        Object.values(data || {}).forEach((articles: any) => {
            if (Array.isArray(articles)) {
                articles.forEach((article: any) => {
                    if (article?.key === 'material_vat' && article?.isTaxFreeArticle && article?.isArticleShowInInvoice) {
                        const value = parseFloat(article?.value) || 0;
                        total += value;
                    }
                });
            }
        });
        return total;
    };

    const otherArticlesSum = Number(calculateTotalSum(otherArticlesData, currencyGlobalValue));
    const expenditureSum = Number(calculateExpenditureSum(otherArticlesData));

    // Update dataRef whenever critical data changes (for sending invoice)
    useEffect(() => {
        if (dataRef) {
            // Get working hours from serviceTableDataByFreelancer (with workingHour field) or fallback to invoiceHours
            let workingHoursToUse = invoiceHours || [];
            
            // If we have serviceTableDataByFreelancer, extract working hours from it (preferred source)
            if (serviceTableDataByFreelancer && Object.keys(serviceTableDataByFreelancer).length > 0) {
                const firstFreelancerKey = Object.keys(serviceTableDataByFreelancer)[0];
                const freelancerData = serviceTableDataByFreelancer[firstFreelancerKey];
                if (freelancerData?.workingHours) {
                    workingHoursToUse = freelancerData.workingHours;
                }
            }
            
            dataRef.current = {
                formData: {
                    ...formData,
                    expressPayout: expressPayout || false, // Add expressPayout from watched form value
                    expressFee: (expressPayout || false) ? 5.9 : 0, // Add calculated expressFee
                    showDates: showDates, // Add showDates from watched form value
                },
                preferences: formData.preferences,
                serviceData: {
                    workingHours: workingHoursToUse, // Use working hours with workingHour field
                    totalAmount: amount || 0,
                },
                serviceTableDataByFreelancer, // NEW - Include service table data by freelancer
                pensionDataByFreelancer, // NEW - Include pension data by freelancer
                holidayDataByFreelancer, // NEW - Include holiday data by freelancer
                deductionsByFreelancer, // NEW - Include deductions by freelancer
                otherArticlesData, // ✅ Include tax-free articles (mileage, travel, allowances, materials)
                calculatedAmounts,
                // Real selected freelancers (single = 1, collective = >1) so the
                // payload builder can take the correct branch for pension/holiday.
                selectedFreelancersForInvoice: selectedFreelancersForInvoice || [],
            };
        }
    }, [dataRef, formData, invoiceHours, amount, serviceTableDataByFreelancer, pensionDataByFreelancer, holidayDataByFreelancer, deductionsByFreelancer, otherArticlesData, calculatedAmounts, expressPayout, showDates, selectedFreelancersForInvoice]);

    // ── Dirty tracking (edit mode) ───────────────────────────────────────────────
    // The "Edit Request Invoice" button stays disabled until the user actually
    // changes something. Instead of diffing the raw state — which is fragile because
    // the same data lives in different containers (e.g. a loaded deduction moves from
    // copiedInvoiceData into deductionsByFreelancer once edited and never moves back)
    // — we snapshot the MERGED outgoing payload. buildInvoicePayload combines every
    // source into one consistent shape and recomputes all amounts, so reverting a
    // change rebuilds the exact same payload as the baseline. Only the
    // non-deterministic bits (dateId's uuid, createdDate's timestamp) are ignored via
    // canonicalizeForDirty. A settle-debounce captures the baseline only once the
    // state (and the payload it builds) has stopped churning after hydration.
    const dirtyBaselineRef = React.useRef<string | null>(null);
    const dirtyTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const dirtyLastReportedRef = React.useRef<boolean>(false);
    const getPayloadRef = React.useRef(getPayload);
    getPayloadRef.current = getPayload;
    useEffect(() => {
        if (!isEditMode) return;
        const buildPayload = getPayloadRef.current;
        if (!buildPayload) return;
        let cancelled = false;
        // Debounce: coalesce rapid edits and let the state (and the dataRef the builder
        // reads) settle before building. During hydration this keeps resetting until
        // things stop changing, so the first settled build becomes the baseline.
        if (dirtyTimerRef.current) clearTimeout(dirtyTimerRef.current);
        dirtyTimerRef.current = setTimeout(async () => {
            let payload: any;
            try { payload = await buildPayload(); } catch { return; }
            if (cancelled || !payload) return;
            // The supporting file isn't in the projected payload (its uploaded name only
            // exists after a real upload, which we deliberately skip here), so detect it
            // straight from formData by its file identity — available the moment it's added.
            const supportingFiles = (formData?.otherDetails?.supportingFiles || []).map((f: any) => ({
                name: f?.file?.name || f?.fileName || '',
                size: f?.file?.size || 0,
            }));
            // Selected customer's details (name/address live on selectedCustomer; the full
            // record — email, VAT, org no, city… — lives in the customers list). Merge both
            // so any customer edit is detected.
            const selCustId = (selectedCustomerRedux?.id ?? (selectedCustomerRedux as any)?.customerId)?.toString();
            const fullCustomer = (customersList || []).find((c: any) =>
                c?.id?.toString() === selCustId || c?.customerId?.toString() === selCustId
            );
            const customer = projectCustomerForDirty({ ...(fullCustomer || {}), ...(selectedCustomerRedux || {}) });
            const snap = JSON.stringify(canonicalizeForDirty({
                payload: projectPayloadForDirty(payload),
                supportingFiles,
                customer,
            }));
            if (dirtyBaselineRef.current === null) {
                dirtyBaselineRef.current = snap; // first settled build after load = baseline
                if (dirtyLastReportedRef.current !== false) {
                    dirtyLastReportedRef.current = false;
                    onDirtyChange?.(false);
                }
                return;
            }
            const dirty = snap !== dirtyBaselineRef.current;
            if (dirty !== dirtyLastReportedRef.current) {
                dirtyLastReportedRef.current = dirty;
                onDirtyChange?.(dirty);
            }
        }, 700);
        return () => { cancelled = true; };
    }, [isEditMode, formData, vat, watchedCurrency, pension, holidaySavings, isFixed, rutDeduction, rootDeduction, expressPayout, showDates, selectedCustomer, selectedCustomerRedux, customersList, serviceTableDataByFreelancer, pensionDataByFreelancer, holidayDataByFreelancer, deductionsByFreelancer, otherArticlesData, selectedFreelancersForInvoice, onDirtyChange]);

    // Clear the debounce timer on unmount
    useEffect(() => () => { if (dirtyTimerRef.current) clearTimeout(dirtyTimerRef.current); }, []);

    // Send deductionsByFreelancer to parent component
    useEffect(() => {
        if (onDeductionsByFreelancerChange) {
            onDeductionsByFreelancerChange(deductionsByFreelancer);
        }
    }, [deductionsByFreelancer, onDeductionsByFreelancerChange]);

    // Real-time invoice calculations (matching InvoiceTool.js lines 474-512)
    useEffect(() => {

        const getData = async () => {
           
            if (!amount || !globalData?.tax?.globalValue || !globalData?.fee?.globalValue) {
                return;
            }
            const payload = {
                vat: Number(vat) || 0,
                currency,
                currencyGlobalValue,
                globalData,
                pension,
                pensionAmount,
                holidaySavings,
                holidaySavingFee,
                rutDeduction,
                rootDeduction,
                expressPayout,
                expenditureSum,
                allowancePerDaySweden,
                anyArrayHasValue,
                selectedCustomer,
                customer,
                isPreliminaryInvoice: isPre,
                prefillingData,
            };

            try {
                const startTime = performance.now();
                
                const { calculations, total } = await calculateMultipleUsers(
                    invoiceHours || [{}],
                    otherArticlesData ? Object.values(otherArticlesData) : [],
                    payload
                );

                const endTime = performance.now();
                const duration = (endTime - startTime).toFixed(2);
                setSeperatedUserCalculations(calculations);
                setCalculatedAmounts(total || {
                    net: 0,
                    tax: 0,
                    amount: 0,
                    pensionPercentage: 0,
                });
            } catch (error) {
                console.error('❌ Invoice Calculations API: Error', {
                    error: error,
                    message: error instanceof Error ? error.message : 'Unknown error',
                    timestamp: new Date().toISOString()
                });
                toast.error(t('FAILED_TO_CALCULATE_INVOICE') as string);
            }
        };

        if (amount && globalData?.tax?.globalValue && globalData?.fee?.globalValue) {
            getData();
        }
    }, [getCalculations, anyArrayHasValue, isPre, grossSalary, serviceTableTrigger]);

    // Debounced calculation trigger (800ms delay like InvoiceTool.js line 516-523)
    // Triggers API call to: /api/invoice/invoicecalculations
    // Same timing and conditions as InvoiceTool.js
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setGetCalculations((prev) => !prev);
        }, 800);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [
        globalData,
        amount,
        pension,
        currency,
        rutDeduction,
        rootDeduction,
        holidaySavings,
        pensionFee,
        pensionAmount,
        vat,
        otherArticlesSum,
        expressPayout,
        holidaySavingFee,
        anyArrayHasValue, // Added to match InvoiceTool.js
        invoiceHours,     // Added to match InvoiceTool.js
    ]);

    const addEmail = () => {
        if (newEmail && !formData.otherDetails.communicationEmails.includes(newEmail)) {
            setFormData(prev => ({
                ...prev,
                otherDetails: {
                    ...prev.otherDetails,
                    communicationEmails: [...prev.otherDetails.communicationEmails, newEmail]
                }
            }));
            setNewEmail('');
        }
    };

    const removeEmail = (emailToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            otherDetails: {
                ...prev.otherDetails,
                communicationEmails: prev.otherDetails.communicationEmails.filter(email => email !== emailToRemove)
            }
        }));
    };

    // Handle customer notes change
    const handleCustomerNotesChange = (notes: string) => {
        setFormData(prev => {
            const updated = {
                ...prev,
                otherDetails: {
                    ...prev.otherDetails,
                    customerNotes: notes
                }
            };
           return updated;
        });
    };

    // Handle supporting file change. Uploads to blob storage immediately on pick
    // (not deferred to Preview/Send/Save Draft) so the resulting blob fileName is
    // ready the moment it's needed — for submission, and so the file can be
    // downloaded again via a fresh read URL even before the invoice is saved.
    // No base64 conversion happens at all: nothing beyond the upload itself is
    // ever read into memory as bytes on the client.
    const handleSupportingFileChange = async (file: File | null) => {
        if (!file) {
            setFormData(prev => ({
                ...prev,
                otherDetails: { ...prev.otherDetails, supportingFiles: [] }
            }));
            return;
        }

        // Show the picked file immediately (native name, upload still pending).
        setFormData(prev => ({
            ...prev,
            otherDetails: {
                ...prev.otherDetails,
                supportingFiles: [{ fileName: file.name, file, uploaded: false }]
            }
        }));

        setIsUploadingSupportingFile(true);
        try {
            const result = await uploadAttachment(file);
            setFormData(prev => ({
                ...prev,
                otherDetails: {
                    ...prev.otherDetails,
                    supportingFiles: prev.otherDetails.supportingFiles[0]?.file === file
                        ? [{ fileName: result.fileName, file, uploaded: true }]
                        : prev.otherDetails.supportingFiles
                }
            }));
        } catch (error) {
            console.error('❌ Error uploading supporting file:', error);
            toast.error(t('FILE_UPLOAD_ERROR') as string);
            setFormData(prev => ({
                ...prev,
                otherDetails: {
                    ...prev.otherDetails,
                    supportingFiles: prev.otherDetails.supportingFiles[0]?.file === file
                        ? []
                        : prev.otherDetails.supportingFiles
                }
            }));
        } finally {
            setIsUploadingSupportingFile(false);
        }
    };

    // Customer selection with VAT calculation (matching InvoiceTool.js lines 1114-1146)
    const handleTypeSelect = async (value: any, bool?: boolean) => {
        if (!value) {
            return;
        }
        
        // Calculate VAT based on customer country
        // Sweden = 25% VAT
        // Other countries = 0% VAT
        const vat = value?.country === 'Sweden' ? 25 : 0;
        setValue("vat", vat);
        
        setCustomer({ customerId: customer?.customerId, ...value });
        setValue("selectedCustomer", value);
        
        // Update form data
        setFormData(prev => ({
            ...prev,
            customer: {
                name: value.customerName || value.name || value.businessType || '',
                id: `#${value.customerId || value.id || ''}`,
                billingAddress: value.billingAddress || `${value.addressLine1 || ''}\n${value.city || ''}, ${value.zipCode || ''}\n${value.country || ''}`,
                accountType: value.accountType || ''
            }
        }));
        
        // toast.success(t('Customer selected successfully') as string);
    };

    // Amount validation (matching InvoiceTool.js lines 1558-1588)
    const amountValidation = useCallback((calledForShowInvoice?: boolean, calledForSaveInvoice?: boolean) => {
        const totalAmountValue = Number(getValues("amount"));
        
        if (!selectedCustomer) {
            toast.error(t('PLEASE_SELECT_CUSTOMER') as string);
            return false;
        }
        
        if (!totalAmountValue || totalAmountValue <= 0) {
            toast.error(t('PLEASE_ENTER_VALID_AMOUNT') as string);
            return false;
        }
        
        // Check amount threshold based on currency
        const thresholds: { [key: string]: number } = {
            'SEK': 150000,
            'EUR': 15000,
            'USD': 15000,
        };
        
        const threshold = thresholds[currency] || 150000;
        
        if (totalAmountValue > threshold) {
            setShowTooltipError({ amountExceedsThreshold: true });
            setTimeout(() => {
                setShowTooltipError({});
            }, 5000);
            return false;
        }
        
        return true;
    }, [selectedCustomer, getValues, currency, t]);

    // Handle fixed price mode (matching InvoiceTool.js lines 1275-1321)
    const handleChangeFixedPrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isFixed = event.target.checked;
        setValue('isFixed', isFixed);
        
        if (isFixed) {
            // Distribute amount across items/freelancers
            const invoiceHours = watch("invoiceHours");
            if (Array.isArray(invoiceHours) && invoiceHours.length > 0) {
                const totalAmount = Number(amount) || 0;
                const amountPerItem = totalAmount / invoiceHours.length;
                
                invoiceHours.forEach((item: any, index: number) => {
                    // Use setValue with any type to bypass strict typing
                    (setValue as any)(`invoiceHours[${index}].amount`, amountPerItem);
                });
            }
        }
    };

    // Handle amount changes (matching InvoiceTool.js lines 1404-1428)
    const handleChangeAmount = () => {
        const invoiceHours = watch("invoiceHours");
        if (!invoiceHours && !anyArrayHasValue) return;
        
        let totalAmount = 0;
        let hourlyAmount = 0;
        
        invoiceHours?.forEach((freelancer: any) => {
            Object.values(freelancer || {}).forEach((item: any) => {
                if (item?.amount) {
                    totalAmount += Number(item.amount);
                    if (item?.article?.articleNumber === "92") {
                        hourlyAmount += Number(item.amount);
                    }
                }
            });
        });
        
        if (anyArrayHasValue) totalAmount += otherArticlesSum;
        setArticleHourlyAmount(hourlyAmount);
        
        if (!isFixed) {
            setValue('amount', totalAmount);
        }
    };

    // Handle unit changes (matching InvoiceTool.js lines 1366-1396)
    const handleChangeUnit = () => {
        let totalAmount = 0;
        let hourlyAmount = 0;
        const invoiceHours = watch("invoiceHours");
        
        if (invoiceHours) {
            invoiceHours.forEach((item: any) => {
                Object.values(item || {}).forEach((field: any) => {
                    if (field?.units && field?.amountPerUnit) {
                        const amount = Number(field.units) * Number(field.amountPerUnit);
                        totalAmount += amount;
                        if (field?.article?.articleNumber === "92") {
                            hourlyAmount += amount;
                        }
                    }
                });
            });
        }
        
        setArticleHourlyAmount(hourlyAmount);
        
        if (!isFixed) {
            setValue('amount', totalAmount + otherArticlesSum);
        }
    };

    const addNewRow = () => {
        const newItem = {
            id: Date.now().toString(),
            name: '',
            quantity: 0,
            price: 0,
            discount: { value: 0, type: 'percentage' as 'percentage' | 'fixed' },
            subtotal: 0
        };
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));
    };

    const removeItem = (itemId: string) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter(item => item.id !== itemId)
        }));
    };

    // Handle service table changes - triggers API recalculation
    const handleServiceTableChange = useCallback(() => {
      
        // Toggle the trigger to force recalculation
        setServiceTableTrigger(prev => {
            const newValue = !prev;
            return newValue;
        });
    }, []); // Empty deps - function doesn't depend on any props/state

    const handleAddCustomer = (customerData: any) => {
       
        // Update the form data with the new customer information
        setFormData(prev => ({
            ...prev,
            customer: {
                name: customerData.customerName || customerData.businessType,
                id: `#${Math.floor(Math.random() * 100000)}`,
                billingAddress: `${customerData.addressLine1}\n${customerData.city}, ${customerData.zipCode}\n${customerData.country}`,
                accountType: customerData.accountType || ''
            }
        }));
    };

    // Handle copy recent invoice button click
    const handleCopyRecentInvoiceClick = () => {
        setShowCopyModal(true);
    };

    // Handle invoice selection from modal
    const handleInvoiceSelect = (invoice: any, fullInvoiceData?: any) => {
        if (fullInvoiceData) {
            // Store the full invoice data in state
            setCopiedInvoiceData(fullInvoiceData);
            
            // Populate form with invoice data
            try {
                // Set customer data
                if (fullInvoiceData.customer) {
                    setCustomer(fullInvoiceData.customer);
                    setValue('selectedCustomer', fullInvoiceData.customer);
                }
                
                // Set currency and VAT
                if (fullInvoiceData.currency) {
                    setValue('currency', fullInvoiceData.currency);
                }
                if (fullInvoiceData.vatPercent !== undefined) {
                    setValue('vat', fullInvoiceData.vatPercent);
                }
                
                // Set pricing type and engagement type
                if (fullInvoiceData.pricingType) {
                    setValue('isFixed', fullInvoiceData.pricingType === 'Fixed');
                }
                
                // Set deductions
                if (fullInvoiceData.rutDeduction !== undefined) {
                    setValue('rutDeduction', fullInvoiceData.rutDeduction);
                }
                if (fullInvoiceData.rotDeduction !== undefined) {
                    setValue('rootDeduction', fullInvoiceData.rotDeduction);
                }
                
                // Set additional options
                if (fullInvoiceData.pension !== undefined) {
                    setValue('pension', fullInvoiceData.pension);
                }
                if (fullInvoiceData.holidaySavings !== undefined) {
                    setValue('holidaySavings', fullInvoiceData.holidaySavings);
                }
                
                toast.success(t('INVOICE_DATA_COPIED_SUCCESSFULLY', { invoiceNumber: invoice.invoiceNumber }) as string);
            } catch (error) {
                console.error('Error populating invoice data:', error);
                toast.error(t('FAILED_TO_COPY_INVOICE_DATA') as string);
            }
        } else {
            toast.success(t('INVOICE_SELECTED', { invoiceNumber: invoice.invoiceNumber }) as string);
        }
    };

    // ── Wizard step navigation ────────────────────────────────────────────────
    const wizardSteps: WizardStep[] = React.useMemo(() => ([
        { key: 'customer',     title: t('WIZARD_STEP_CUSTOMER_TITLE') as string, subtitle: t('WIZARD_STEP_CUSTOMER_SUB') as string },
        { key: 'collaborators',title: t('WIZARD_STEP_COLLAB_TITLE') as string,    subtitle: t('WIZARD_STEP_COLLAB_SUB') as string },
        { key: 'preferences',  title: t('WIZARD_STEP_PREF_TITLE') as string,      subtitle: t('WIZARD_STEP_PREF_SUB') as string },
        { key: 'items',        title: t('WIZARD_STEP_ITEMS_TITLE') as string,     subtitle: t('WIZARD_STEP_ITEMS_SUB') as string },
        { key: 'summary',      title: t('WIZARD_STEP_SUMMARY_TITLE') as string,   subtitle: t('WIZARD_STEP_SUMMARY_SUB') as string },
        { key: 'review',       title: t('DESCRIPTION') as string,                 subtitle: t('WIZARD_STEP_REVIEW_SUB') as string },
    ]), [t]);

    const totalSteps = wizardSteps.length;
    const [activeStep, setActiveStep] = useState(0);
    const [maxReached, setMaxReached] = useState(0);
    const wizardTopRef = React.useRef<HTMLDivElement>(null);

    // First-mount shimmer skeleton (revealed after ~1s); page chrome stays visible
    const [showSkeleton, setShowSkeleton] = useState(true);
    useEffect(() => {
        const timer = setTimeout(() => setShowSkeleton(false), 1000);
        return () => clearTimeout(timer);
    }, []);

    // On mobile the workflow bar is pinned just above the (variable-height) footer.
    // Measure the footer live and expose its height as --iw-footer-h so CSS can offset.
    useEffect(() => {
        const root = wizardTopRef.current;
        if (!root) return;
        const update = () => {
            const footer = document.querySelector('.invoice-footer') as HTMLElement | null;
            // Space the footer occupies from the viewport bottom (height + its bottom margin),
            // so the workflow bar can sit flush right on top of it.
            const gap = footer ? Math.max(0, window.innerHeight - footer.getBoundingClientRect().top) : 0;
            root.style.setProperty('--iw-footer-h', `${gap}px`);
        };
        update();
        let ro: ResizeObserver | undefined;
        const footer = document.querySelector('.invoice-footer');
        if (footer && typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(update);
            ro.observe(footer);
        }
        window.addEventListener('resize', update);
        return () => { ro?.disconnect(); window.removeEventListener('resize', update); };
    }, []);

    const scrollWizardToTop = () => {
        // Scroll the stepper into view so the new step starts from the top
        if (wizardTopRef.current) {
            const prefersReduced = typeof window !== 'undefined' &&
                window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
            wizardTopRef.current.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
        }
    };

    // Scroll a given section into view (single-page scroll layout)
    const scrollToPane = (index: number) => {
        const container = wizardTopRef.current;
        const panes = container?.querySelectorAll('.iw-step-pane');
        const target = panes?.[index] as HTMLElement | undefined;
        if (target) {
            const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
            target.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
        }
    };

    // ── Collective invoice toggle (shows/hides the Collaborators section) ──────
    const [collectiveEnabled, setCollectiveEnabled] = useState(false);

    // Auto-enable when an invoice is loaded/copied that already has collaborators
    useEffect(() => {
        if (selectedFreelancersForInvoice.length > 1) setCollectiveEnabled(true);
    }, [selectedFreelancersForInvoice.length]);

    const handleToggleCollective = () => {
        setCollectiveEnabled((prev) => {
            const next = !prev;
            if (next) {
                // Turning ON → reveal + scroll to the Collaborators section.
                // But NOT during the guided tour (the auto-scroll would jump the
                // page away from the current tour step).
                setActiveStep(1);
                setMaxReached((m) => Math.max(m, 1));
                const tourActive = typeof document !== 'undefined' && !!document.querySelector('.iv-tour');
                if (!tourActive) setTimeout(() => scrollToPane(1), 70);
            } else {
                // Turning OFF → clear the added collaborators (keep only "you")
                dispatch(setSelectedFreelancersForInvoice(selectedFreelancersForInvoice.slice(0, 1)));
            }
            return next;
        });
    };

    const goToStep = (index: number) => {
        if (index < 0 || index > totalSteps - 1) return;
        const needEnable = index === 1 && !collectiveEnabled;
        if (needEnable) {
            setCollectiveEnabled(true);
            setMaxReached((m) => Math.max(m, 1));
        }
        setActiveStep(index);
        setMaxReached((m) => Math.max(m, index));
        // If we just revealed the Collaborators section, wait a tick before scrolling
        if (needEnable) setTimeout(() => scrollToPane(index), 70);
        else scrollToPane(index);
    };

    // All sections are on one page, so every step is directly reachable
    useEffect(() => {
        setMaxReached(totalSteps - 1);
    }, [totalSteps]);

    // Scroll-spy — highlight the step whose section is currently in view
    useEffect(() => {
        const container = wizardTopRef.current;
        if (!container) return;
        const panes = Array.from(container.querySelectorAll('.iw-step-pane')) as HTMLElement[];
        if (panes.length === 0) return;
        const observer = new IntersectionObserver(
            (entries) => {
                const inView = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (inView.length > 0) {
                    const idx = panes.indexOf(inView[0].target as HTMLElement);
                    if (idx >= 0) setActiveStep(idx);
                }
            },
            { root: null, rootMargin: '-16% 0px -74% 0px', threshold: 0 }
        );
        panes.forEach((p) => observer.observe(p));

        // The top detection band can't reach the LAST section when the page is
        // scrolled all the way down, so it would stay stuck on the previous step.
        // When the scroll container hits (near) the bottom, force the last visible
        // pane active. Guarded by a real-overflow check so short pages are unaffected.
        const scrollEl = document.querySelector('.create-invoice-page') as HTMLElement | null;
        const onScroll = () => {
            if (!scrollEl) return;
            const hasOverflow = scrollEl.scrollHeight - scrollEl.clientHeight > 40;
            if (!hasOverflow) return;
            if (scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 6) {
                const visible = panes.filter((p) => p.offsetParent !== null); // skip display:none panes
                const last = visible[visible.length - 1];
                const idx = last ? panes.indexOf(last) : -1;
                if (idx >= 0) setActiveStep(idx);
            }
        };
        scrollEl?.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            observer.disconnect();
            scrollEl?.removeEventListener('scroll', onScroll);
        };
    }, [totalSteps]);

    const handleWizardNext = () => {
        setActiveStep((prev) => {
            const next = Math.min(prev + 1, totalSteps - 1);
            setMaxReached((m) => Math.max(m, next));
            return next;
        });
        scrollWizardToTop();
    };

    const handleWizardBack = () => {
        setActiveStep((prev) => Math.max(prev - 1, 0));
        scrollWizardToTop();
    };

    // Expose "Back" so the page-level footer can drive it on the final step
    useEffect(() => {
        if (backHandlerRef) backHandlerRef.current = handleWizardBack;
        return () => { if (backHandlerRef) backHandlerRef.current = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [backHandlerRef]);

    const paneClass = (index: number) => `iw-step-pane${activeStep === index ? ' is-active' : ''}`;

    // ── Step completion — the green tick shows only when a step is actually filled ──
    const customerName = (formData?.customer?.name || '').trim();
    const customerDone = !!(customerName || selectedCustomer || formData?.customer?.id);
    const serviceCount = Object.values(serviceTableDataByFreelancer || {}).reduce(
        (n: number, d: any) => n + (Array.isArray(d?.workingHours) ? d.workingHours.length : 0),
        0
    );
    const itemsDone = serviceCount > 0;
    // Any row still missing a required field (reported by the services table)?
    const itemsHasIncomplete = Object.values(serviceTableDataByFreelancer || {}).some(
        (d: any) => d?.hasIncompleteRows
    );
    const notesText = (formData?.otherDetails?.customerNotes || '').trim();
    // Description is valid once it has at least 20 characters OR at least 3 words.
    const notesWordCount = notesText ? notesText.split(/\s+/).filter(Boolean).length : 0;
    const detailsValid = notesText.length >= 20 || notesWordCount >= 3;
    const detailsHasText = notesText.length > 0;
    const detailsDone = detailsValid;

    // ── Live, contextual subtitles for the workflow sidebar (mirrors the actual data) ──
    const collabCount = Array.isArray(selectedFreelancersForInvoice) ? selectedFreelancersForInvoice.length : 0;
    // Prefer the live preferences values (updated on change) over the RHF-watched
    // fields, which aren't re-set when VAT/currency change via the preferences UI.
    const currencyDisplay = formData?.preferences?.currency || watchedCurrency || 'SEK';
    const prefVat = formData?.preferences?.vat;
    const vatDisplayRaw = (prefVat !== undefined && prefVat !== null && (prefVat as any) !== '') ? prefVat : (vat ?? 25);
    const truncate = (s: string, n = 22) => (s.length > n ? `${s.slice(0, n).trim()}…` : s);

    // Completion for the remaining steps (green tick when their data is filled):
    // Collaborators — has ≥2 people; Preferences — currency + VAT set; Payment — total > 0.
    const collabDone = collabCount > 1;
    const prefsDone = !!currencyDisplay && vatDisplayRaw !== undefined && vatDisplayRaw !== null && `${vatDisplayRaw}` !== '';
    const paymentTotalRaw = calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat;
    const paymentDone = paymentTotalRaw !== undefined && paymentTotalRaw !== null && Number(paymentTotalRaw) > 0;
    const stepStatus = (index: number): { subtitle?: string; warn?: boolean; error?: boolean } => {
        switch (index) {
            case 0: return { subtitle: customerDone ? customerName : (t('WF_SELECT_CUSTOMER') as string) };
            case 1: return { subtitle: collabCount > 1 ? `${collabCount} ${t('WF_PEOPLE')}` : (t('WF_JUST_YOU') as string) };
            case 2: return { subtitle: `${currencyDisplay} · ${t('VAT')} ${vatDisplayRaw}%` };
            case 3:
                if (!itemsDone) return { subtitle: t('WF_NO_SERVICES') as string, warn: true };
                // Rows exist but some are missing required fields → red error.
                if (itemsHasIncomplete) return { subtitle: t('WF_ITEMS_INCOMPLETE') as string, error: true };
                return { subtitle: `${serviceCount} ${serviceCount === 1 ? t('WF_SERVICE_ONE') : t('WF_SERVICE_MANY')}` };
            case 4: {
                const calc = calculatedSubAmount?.response?.totalCalculation;
                const total = calc?.amountIncludingVat;
                const hasTotal = total !== undefined && total !== null && Number(total) > 0;
                return {
                    subtitle: hasTotal
                        ? formatAmountWithLocale(Number(total), calc?.currencyName || currencyDisplay, i18n.language)
                        : (t('WF_PENDING') as string),
                    warn: !hasTotal,
                };
            }
            case 5:
                // Description: Done (valid) / red hint (has text but too short) / amber pending (empty).
                if (detailsValid) return { subtitle: t('WF_DONE') as string };
                if (detailsHasText) return { subtitle: t('WF_DESC_MIN_HINT') as string, error: true };
                return { subtitle: t('WF_PENDING') as string, warn: true };
            default: return {};
        }
    };

    // Report active step (+ its live context) to the page so the footer can show
    // the step context in place of the Grand Total on mobile.
    const activeStepTitle = wizardSteps[activeStep]?.title || '';
    const activeStepSubtitle = stepStatus(activeStep).subtitle || '';
    useEffect(() => {
        if (onStepChange) onStepChange(activeStep, totalSteps, activeStepTitle, activeStepSubtitle);
    }, [activeStep, totalSteps, activeStepTitle, activeStepSubtitle, onStepChange]);

    return (
        <Container  className="create-invoice-wrapper">
            {/* Loading overlay for calculations */}
            {calcLoading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">{t('LOADING')}</span>
                    </div>
                </div>
            )}
            
            <div className={`invoice-wizard iw-scroll${showSkeleton ? '' : ' iw-revealed'}`} ref={wizardTopRef}>
            {showSkeleton && (
                <div className="iw-skel-overlay" aria-hidden="true">
                    <div className="iw-skel-layout">
                        <div className="iw-skel-side">
                            <div className="iw-skel iw-skel-step" style={{ width: '70%' }} />
                            <div className="iw-skel iw-skel-step" />
                            <div className="iw-skel iw-skel-step" />
                            <div className="iw-skel iw-skel-step" />
                            <div className="iw-skel iw-skel-step" />
                            <div className="iw-skel iw-skel-step" />
                        </div>
                        <div className="iw-skel-main">
                            <div className="iw-skel iw-skel-header" />
                            <div className="iw-skel-toolbar">
                                <div className="iw-skel iw-skel-tool" />
                                <div className="iw-skel iw-skel-tool" />
                                <div className="iw-skel iw-skel-tool" />
                            </div>
                            <div className="iw-skel-card">
                                <div className="iw-skel iw-skel-tablehead" />
                                {Array.from({ length: 7 }).map((_, i) => (
                                    <div key={i} className="iw-skel iw-skel-row" />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="iw-scroll-layout">
            <aside className="iw-scroll-side">
            <InvoiceWizardStepper
                steps={wizardSteps.map((s, i) => {
                    const patch: Partial<WizardStep> = {};
                    if (i === 1) patch.hidden = !collectiveEnabled;
                    if (i === 0) patch.done = customerDone;   // Customer — only when a customer is selected
                    if (i === 1) patch.done = collabDone;     // Collaborators — when ≥2 people are added
                    if (i === 2) patch.done = prefsDone;      // Preferences — when currency + VAT are set
                    if (i === 3) patch.done = itemsDone && !itemsHasIncomplete;   // Items — ≥1 row and none incomplete
                    if (i === 4) patch.done = paymentDone;    // Payment & Summary — when the total is calculated
                    if (i === 5) patch.done = detailsDone;    // Other Details — only when description is filled
                    // Live contextual subtitle (customer name, "SEK · VAT 6%", "No services yet"…)
                    const status = stepStatus(i);
                    if (status.subtitle) patch.subtitle = status.subtitle;
                    if (status.warn) patch.warn = true;
                    if (status.error) patch.error = true;
                    return Object.keys(patch).length ? { ...s, ...patch } : s;
                })}
                activeStep={activeStep}
                maxReached={maxReached}
                onStepClick={goToStep}
                stepOfLabel={t('WIZARD_STEP_OF', { current: activeStep + 1, total: totalSteps }) as string}
                workflowLabel={t('WORKFLOW') as string}
            />
            </aside>
            <div className="iw-scroll-main">

            {/* Quick-setup row — logo / copy last invoice / collective toggle */}
            <div className="iw-quick-row">
                {/* Company logo */}
                <div
                    className={`iw-quick-card iw-quick-logo${dragActive ? ' drag-active' : ''}${logoPreview ? ' has-logo' : ''}`}
                    onClick={handleLogoClick}
                    onDragEnter={handleDragIn}
                    onDragLeave={handleDragOut}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') handleLogoClick(); }}
                >
                    <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} disabled={logoLoading} />
                    <span className="iw-quick-ic">
                        {logoLoading ? (
                            <span className="spinner-border spinner-border-sm" role="status" />
                        ) : logoPreview ? (
                            <img src={logoPreview} alt="Logo" className="iw-quick-logo-img" />
                        ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M17 8l-5-5-5 5" /><path d="M12 3v12" /></svg>
                        )}
                    </span>
                    <span className="iw-quick-text">
                        <strong>{t('COMPANY_LOGO')}</strong>
                        <span>{logoPreview ? t('LOGO_CLICK_TO_CHANGE') : t('LOGO_OPTIONAL_FORMATS')}</span>
                    </span>
                    {logoPreview && logoPreview !== DEFAULT_LOGO && !logoLoading && (
                        <button type="button" className="iw-quick-remove" onClick={handleRemoveLogo} aria-label="Remove logo">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>

                {/* Copy last invoice */}
                {!isEditMode && (
                    <button type="button" className="iw-quick-card" data-tour="tour-copy-last" onClick={handleCopyRecentInvoiceClick}>
                        <span className="iw-quick-ic">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                        </span>
                        <span className="iw-quick-text">
                            <strong>{t('COPY_LAST_INVOICE')}</strong>
                            <span>{t('COPY_LAST_INVOICE_DESC')}</span>
                        </span>
                    </button>
                )}

                {/* Collective invoice — toggle shows/hides the Collaborators section.
                    Disabled during an edit request: freelancers can only be removed
                    (from the Collaborators list), never added/toggled, at edit time. */}
                <div
                    className={`iw-quick-card iw-quick-collective${collectiveEnabled ? ' is-active' : ''}${isEditMode ? ' is-disabled' : ''}`}
                    onClick={isEditMode ? undefined : handleToggleCollective}
                    role="button"
                    tabIndex={isEditMode ? -1 : 0}
                    aria-pressed={collectiveEnabled}
                    aria-disabled={isEditMode || undefined}
                    title={isEditMode ? (t('COLLECTIVE_INVOICE_LOCKED_EDIT') || 'Freelancers can\'t be changed while editing an invoice') : undefined}
                    onKeyPress={(e) => { if (!isEditMode && (e.key === 'Enter' || e.key === ' ')) handleToggleCollective(); }}
                >
                    <span className="iw-quick-ic collective">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                    </span>
                    <span className="iw-quick-text">
                        <strong className="iw-quick-title-row">
                            {t('COLLECTIVE_INVOICE_LABEL')}
                            {collectiveEnabled && <span className="iw-quick-badge">{t('ACTIVE')}</span>}
                        </strong>
                        <span>{t('COLLECTIVE_INVOICE_DESC')}</span>
                    </span>
                    <span className={`iw-quick-toggle${collectiveEnabled ? ' on' : ''}`} aria-hidden="true"><span className="iw-quick-knob" /></span>
                </div>
            </div>

            {/* Old header band (logo dropzone + copy button) — replaced by the quick-row above */}
            {false && (
            <Row className="invoice-header">
                <Col md={12} className="w-100 Header_section_top" style={{position: 'relative', right: '10px'}}>
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-lg-center gap-3">
                        <div className="draft-invoice-section flex-grow-1">
                            {/* <div className="draft-invoice-info mb-2 mb-sm-3">
                                <span className="draft-label">{t('DRAFT_INVOICE')} :</span>
                                <span className="invoice-number">INV_25082025_001</span>
                            </div> */}
                            <div 
                                className={`invoice-logo ${dragActive ? 'drag-active' : ''}`}
                                onClick={handleLogoClick}
                                onDragEnter={handleDragIn}
                                onDragLeave={handleDragOut}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                role="button"
                                tabIndex={0}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        handleLogoClick();
                                    }
                                }}
                            >
                                <input
                                    ref={logoInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoChange}
                                    style={{ display: 'none' }}
                                    disabled={logoLoading}
                                />
                                {logoLoading ? (
                                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80px' }}>
                                        <div className="spinner-border spinner-border-sm" role="status">
                                            {/* <span className="visually-hidden">{t('LOADING')}</span> */}
                                        </div>
                                    </div>
                                ) : logoPreview ? (
                                    <div className="logo-preview-container">
                                        <img 
                                            src={logoPreview ?? undefined}
                                            alt="Uploaded Logo" 
                                            className="uploaded-logo-image"
                                        />
                                        {logoPreview !== DEFAULT_LOGO && (
                                        <button
                                            className="logo-remove-button"
                                            onClick={handleRemoveLogo}
                                            type="button"
                                            aria-label="Remove logo"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                        )}
                                        <div className="logo-change-overlay">
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                <path d="M17.5 12.5V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V12.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M14.1667 6.66667L10 2.5L5.83334 6.66667" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M10 2.5V12.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <span className="change-text">{t('CHANGE_LOGO')}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="logo-upload-empty">
                                        <span className="logo-upload-icon" aria-hidden="true">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <path d="M17 8l-5-5-5 5" /><path d="M12 3v12" />
                                            </svg>
                                        </span>
                                        <div className="upload-text">
                                            <span className="drop-text d-none d-sm-inline">{t('DROP_FILE_OR')}</span>
                                            <span className="browse-text d-none d-sm-inline">{t('BROWSE')}</span>
                                            <span className="drop-text d-none d-sm-inline">{t('DROP_FILE_OR_NEXT')}</span>
                                            <span className="attach-logo-text d-block d-sm-none">{t('ATTACH_YOUR_LOGO')}</span>
                                        </div>
                                        <div className="upload-format-info d-none d-sm-block">
                                            {t('LOGO_FORMAT_INFO')}
                                        </div>
                                        <div className="upload-format-info d-block d-sm-none" style={{ fontSize: '10px' }}>
                                            {t('LOGO_FORMAT_INFO_MOBILE')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        {!isEditMode && activeStep === 0 && (
                            <div className="d-flex align-items-center justify-content-center justify-content-lg-end mt-3 mt-lg-0">
                                <Button
                                    variant=""
                                    className="copy-recent-invoice-btn w-100 w-lg-auto"
                                    onClick={handleCopyRecentInvoiceClick}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <path d="M3 1V9.53674e-07H2V1H3ZM3.72801 12.8547L3.27402 13.7457L3.27402 13.7457L3.72801 12.8547ZM3.14532 12.272L2.25432 12.726L2.25432 12.726L3.14532 12.272ZM10.272 1.14533L10.726 0.254319L10.726 0.254319L10.272 1.14533ZM10.8547 1.72801L9.96367 2.182L9.96367 2.182L10.8547 1.72801ZM4 11.6667C4 11.1144 3.55228 10.6667 3 10.6667C2.44771 10.6667 2 11.1144 2 11.6667H3H4ZM3 4C3.55228 4 4 3.55229 4 3C4 2.44772 3.55229 2 3 2L3 3L3 4ZM1.33333 3L1.33333 2L1.33333 3ZM1 2.66667L0 2.66667L1 2.66667ZM1 2.33333L2 2.33333L1 2.33333ZM4.33333 2C4.88562 2 5.33333 1.55228 5.33333 1C5.33333 0.447715 4.88562 -3.95011e-08 4.33333 0L4.33333 1L4.33333 2ZM2.33333 1L2.33333 2L2.33333 2L2.33333 1ZM11 3.13333H10V11H11H12V3.13333H11ZM3 10.8667H4V1H3H2V10.8667H3ZM5 13V12C4.60447 12 4.41608 11.9989 4.28661 11.9864C4.20141 11.9782 4.19531 11.9704 4.182 11.9637L3.72801 12.8547L3.27402 13.7457C3.82044 14.0241 4.34225 14 5 14V13ZM3 10.8667H2C2 11.2235 1.99922 11.5513 2.02148 11.8238C2.0447 12.108 2.09785 12.4189 2.25432 12.726L3.14532 12.272L4.03633 11.818C4.04747 11.8399 4.02795 11.8215 4.01484 11.661C4.00078 11.4888 4 11.2565 4 10.8667H3ZM3.72801 12.8547L4.182 11.9637C4.11928 11.9317 4.06829 11.8807 4.03633 11.818L3.14532 12.272L2.25432 12.726C2.47802 13.165 2.83498 13.522 3.27402 13.7457L3.72801 12.8547ZM8.86666 1V2C9.25653 2 9.48879 2.00078 9.66096 2.01485C9.82146 2.02796 9.83985 2.04747 9.81799 2.03633L10.272 1.14533L10.726 0.254319C10.4189 0.0978562 10.108 0.0447058 9.82383 0.0214876C9.55132 -0.000776752 9.22353 9.53674e-07 8.86666 9.53674e-07V1ZM11 3.13333H12C12 2.77647 12.0008 2.44867 11.9785 2.17617C11.9553 1.89199 11.9021 1.5811 11.7457 1.27402L10.8547 1.72801L9.96367 2.182C9.95253 2.16014 9.97204 2.17854 9.98515 2.33904C9.99922 2.5112 10 2.74346 10 3.13333H11ZM10.272 1.14533L9.81799 2.03633C9.88071 2.06829 9.93171 2.11928 9.96367 2.182L10.8547 1.72801L11.7457 1.27402C11.522 0.834978 11.165 0.478024 10.726 0.254319L10.272 1.14533ZM8.86666 1V9.53674e-07H3V1V2H8.86666V1ZM4.33333 13V14H5V13V12H4.33333V13ZM5 13H6V11.3333H5H4V13H5ZM5.33333 11V12H12.6667V11V10H5.33333V11ZM13 11.3333H12V11.6667H13H14V11.3333H13ZM11.6667 13V12H5V13V14H11.6667V13ZM13 11.6667H12C12 11.8508 11.8508 12 11.6667 12V13V14C12.9553 14 14 12.9553 14 11.6667H13ZM12.6667 11V12C12.2985 12 12 11.7015 12 11.3333H13H14C14 10.597 13.403 10 12.6667 10V11ZM5 11.3333H6C6 11.7015 5.70152 12 5.33333 12V11V10C4.59695 10 4 10.597 4 11.3333H5ZM3 11.6667H2C2 12.9553 3.04467 14 4.33333 14V13V12C4.14924 12 4 11.8508 4 11.6667H3ZM3 3L3 2L1.33333 2L1.33333 3L1.33333 4L3 4L3 3ZM1 2.66667L2 2.66667L2 2.33333L1 2.33333L2.9141e-08 2.33333L0 2.66667L1 2.66667ZM4.33333 1L4.33333 0L2.33333 1.43046e-07L2.33333 1L2.33333 2L4.33333 2L4.33333 1ZM1 2.33333L2 2.33333C2 2.14924 2.14924 2 2.33333 2L2.33333 1L2.33333 1.43046e-07C1.04467 3.03875e-08 1.418e-07 1.04467 2.9141e-08 2.33333L1 2.33333ZM1.33333 3L1.33333 2C1.70153 2 2 2.29848 2 2.66667L1 2.66667L0 2.66667C-6.43762e-08 3.40304 0.596951 4 1.33333 4L1.33333 3Z" fill="#544196"/>
</svg>
                                    <span className="d-none d-sm-inline">{t('COPY_RECENT_INVOICE')}</span>
                                    <span className="d-inline d-sm-none">{t('COPY_RECENT')}</span>
                                </Button>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
            )}

            {/* Step 1 — Customer */}
            <div className={paneClass(0)}>
            <CustomerDetails
                customer={formData.customer}
                onCustomerChange={handleTypeSelect}
                onAddCustomer={handleAddCustomer}
                copiedInvoiceData={copiedInvoiceData}
                isEditModeInvoice={isEditMode}
                timesheetData={timesheetData}
                onCopyRecentInvoice={handleCopyRecentInvoiceClick}
                stepTitle={wizardSteps[0]?.title}
                stepSubtitle={wizardSteps[0]?.subtitle}
            />
            </div>

            {/* Step 2 — Collaborators (shown only when the Collective toggle is on) */}
            <div className={`${paneClass(1)}${collectiveEnabled ? '' : ' iw-pane-hidden'}`}>
            {/* {selectedCustomer && ( */}
                <CollectiveInvoice
                    copiedInvoiceData={copiedInvoiceData}
                    isEditModeInvoice={isEditMode}
                    stepTitle={wizardSteps[1]?.title}
                    stepSubtitle={wizardSteps[1]?.subtitle}
                />
             {/* )}  */}
            </div>

            {/* Step 3 — Preferences */}
            <div className={paneClass(2)}>
            <InvoicePreferences
                preferences={formData.preferences}
                onPreferenceChange={(field, value) => {
                     
                    // Use functional setState to avoid stale closure
                    setFormData(prev => {
                        const updatedPreferences = {
                            ...prev.preferences,
                            [field]: value
                        };
                                               
                        // Notify parent of preferences change
                        if (onPreferencesChange) {
                            onPreferencesChange(updatedPreferences);
                        }
                        
                        const updated = {
                            ...prev,
                            preferences: updatedPreferences
                        };
                        return updated;
                    });
                    
                    // When VAT changes, update VAT in all working hours
                    if (field === 'vat') {
                        const newVat = parseFloat(value);
                        setServiceTableDataByFreelancer((prev: any) => {
                            const updated = { ...prev };
                            Object.keys(updated).forEach(freelancerId => {
                                const freelancerData = updated[freelancerId];
                                if (freelancerData && freelancerData.workingHours) {
                                    freelancerData.workingHours = freelancerData.workingHours.map((wh: any) => {
                                        // Preserve VAT for Travel Expenses PDF (98) and Materials/Tools PDF (99) as they have their own VAT input fields
                                        const shouldPreserveVat = wh.articleNumber === '98' || wh.articleNumber === '99';
                                        return {
                                            ...wh,
                                            vat: shouldPreserveVat ? wh.vat : newVat
                                        };
                                    });
                                }
                            });
                            return updated;
                        });
                    }
                }}
                copiedInvoiceData={copiedInvoiceData}
                isEditModeInvoice={isEditMode}
                hasHourlyArticle={hasHourlyArticle}
                customerInfo={{
                    accountType: selectedCustomer?.accountType,
                    country: selectedCustomer?.country,
                    customerId: selectedCustomer?.customerId ? Number(selectedCustomer.customerId) : undefined
                }}
                stepTitle={wizardSteps[2]?.title}
                stepSubtitle={wizardSteps[2]?.subtitle}
            />
            </div>

            {/* Step 4 — Items */}
            <div className={`${paneClass(3)} iw-items-pane`}>
           <ItemDetails
                stepTitle={wizardSteps[3]?.title}
                stepSubtitle={wizardSteps[3]?.subtitle}
                items={formData.items}
                users={mockUsers}
                articles={mockArticles}
                currency={formData.preferences.currency || 'SEK'}
                control={control}
                register={register}
                watch={watch}
                setValue={setValue}
                errors={errors}
                trigger={trigger}
                t={t}
                showTooltipError={showTooltipError}
                setShowTooltipError={setShowTooltipError}
                preferences={formData.preferences}
                globalData={globalData}
                calculatedAmounts={calculatedAmounts}
                onChangeUnit={handleChangeUnit}
                onChangeAmount={handleChangeAmount}
                onServiceTableChange={handleServiceTableChange}
                timesheetData={timesheetData}
                invoiceNumber={invoiceNumber}
                onServiceTableDataByFreelancerChange={(data) => {
                   setServiceTableDataByFreelancer(data);
                }}
                onPensionDataByFreelancerChange={(data) => {
                  setPensionDataByFreelancer(data);
                }}
                onHolidayDataByFreelancerChange={(data) => {
                     setHolidayDataByFreelancer(data);
                }}
                onDeductionsByFreelancerChange={(data) => {
                   setDeductionsByFreelancer(data);
                    if (onDeductionsByFreelancerChange) {
                        onDeductionsByFreelancerChange(data);
                    }
                }}
                onRemovedCopiedWorkingHoursChange={(removedSet) => {
                    // Store in dataRef for parent access
                    if (dataRef) {
                        dataRef.current = {
                            ...dataRef.current,
                            removedCopiedWorkingHours: removedSet
                        };
                    }
                }}
                onCalculatedSubAmountChange={(data) => {
                   setCalculatedSubAmount(data);
                }}
                copiedInvoiceData={copiedInvoiceData}
                isEditModeInvoice={isEditMode}
                preId={preId}
                validateServiceDescriptionsRef={validateServiceDescriptionsRef}
                validateDatesRef={validateDatesRef}
                createdDate={createdDate}
                // fromData={formData}
            />
            </div>

            {/* Step 5 — Payment & Summary + Express Payment (portaled from Items) */}
            <div className={paneClass(4)}>
                <div className="invoice-card iw-step5-parent">
                    {/* Parent heading covering both Express Payment + the totals */}
                    <div className="cust-step-header iw-head-with-icon">
                        <span className="iw-head-icon" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>
                        </span>
                        <div className="iw-head-text">
                            <h2 className="cust-step-title">{wizardSteps[4]?.title}</h2>
                            <p className="cust-step-sub">{wizardSteps[4]?.subtitle}</p>
                        </div>
                    </div>
                    <div className="iw-step5-grid">
                        {/* Express Payment card is portaled into this slot from ItemDetails */}
                        <div id="iw-express-slot" className="iw-express-slot" />
                        <div className="iw-step5-summary">
                            <WizardSummary
                                calculatedSubAmount={calculatedSubAmount}
                                calculatedAmounts={calculatedAmounts}
                                currency={currency}
                                freelancers={selectedFreelancersForInvoice}
                                currentUser={user}
                                isLoading={calcLoading}
                                expressPayout={expressPayout}
                                language={i18n.language}
                                t={t}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Step 6 — Additional details (the recap is redundant on the single-page
                scroll layout, since every section is already visible above) */}
            <div className={paneClass(5)}>
            <OtherDetails
                otherDetails={formData.otherDetails}
                newEmail={newEmail}
                onNewEmailChange={setNewEmail}
                onAddEmail={addEmail}
                onRemoveEmail={removeEmail}
                onCustomerNotesChange={handleCustomerNotesChange}
                onSupportingFileChange={handleSupportingFileChange}
                isUploadingFile={isUploadingSupportingFile}
                copiedInvoiceData={copiedInvoiceData}
                isEditModeInvoice={isEditMode}
                stepSubtitle={wizardSteps[5]?.subtitle}
            />
            </div>

            {/* Old step-by-step nav — replaced by the single-page scroll layout. */}
            {false && (
            <div className="iw-nav">
                {activeStep > 0 ? (
                    <button type="button" className="iw-btn iw-btn--ghost" onClick={handleWizardBack}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {t('WIZARD_BACK')}
                    </button>
                ) : <span />}
                <span className="iw-nav-indicator">
                    {t('WIZARD_STEP_OF', { current: activeStep + 1, total: totalSteps }) as string}
                </span>
                <button type="button" className="iw-btn iw-btn--primary" onClick={handleWizardNext}>
                    {t('WIZARD_CONTINUE')}
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>
            )}
            </div>{/* iw-scroll-main */}
            </div>{/* iw-scroll-layout */}
            </div>

            {/* Copy Recent Invoice Modal */}
            <CopyRecentInvoiceModal
                show={showCopyModal}
                onClose={() => setShowCopyModal(false)}
                onSelect={handleInvoiceSelect}
            />
        </Container>
    );
};

export default CreateInvoice;